import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { translations } from '../../configs/translations'
import { HallOfFameHistoricalData } from '../../models/HallOfFameHistoricalData'
import RaceService from '../../services/RaceService'
import { useStore } from '../../store/store'
import { Awards } from '../race/finish/awards/Awards'
import { Winner } from '../race/finish/winner/Winner'
import './HallOfFame.css'

type HallStage = 'loading' | 'winner' | 'experience' | 'money' | 'done'

export const HallOfFame = () => {
	const language = useStore((state) => state.language)
	const t = translations[language]

	const [searchParams] = useSearchParams()
	const finishId = searchParams.get('finishId')
	const activityId = searchParams.get('activityId')

	const [awardsData, setAwardsData] = useState<HallOfFameHistoricalData | null>(null)
	const [stage, setStage] = useState<HallStage>('loading')

	const showError = useCallback((error: any) => {
		const errorKey = error?.response?.data as keyof typeof t | undefined

		toast.error(
			errorKey && t[errorKey] ? t[errorKey] : t.error_unknown,
			{ className: 'notification-error' }
		)
	}, [t])

	useEffect(() => {
		if (!finishId && !activityId) return

		if (finishId) {
			RaceService.getHistoricalFinishById(finishId)
				.then((awards) => {
					setAwardsData(awards)
					setStage('winner')
				})
				.catch(showError)
		}

		if (activityId) {
			RaceService.getHistoricalFinishByActivityId(activityId)
				.then((awards) => {
					setAwardsData(awards)
					setStage('winner')
				})
				.catch(showError)
		}
	}, [finishId, activityId, showError])

	const experienceAward = useMemo(() => {
		return awardsData?.awards?.find((award) => award.title === 'XP')
	}, [awardsData])

	const moneyAward = useMemo(() => {
		return awardsData?.awards?.find((award) => award.title === 'MONEY')
	}, [awardsData])

	useEffect(() => {
		const timer = setTimeout(() => {
			if (experienceAward) {
				setStage('experience')
				return
			}

			if (moneyAward) {
				setStage('money')
				return
			}

			setStage('done')
		}, 6000)

		return () => clearTimeout(timer)
	}, [awardsData, stage, experienceAward, moneyAward])

	useEffect(() => {
		if (stage !== 'experience') return

		const timer = setTimeout(() => {
			if (moneyAward) {
				setStage('money')
				return
			}

			setStage('done')
		}, 3000)

		return () => clearTimeout(timer)
	}, [stage, moneyAward])

	const renderContent = () => {
		if (!awardsData || stage === 'loading' || stage === 'done') {
			return null
		}

		if (stage === 'winner') {
			return <Winner name={awardsData.winnerName} />
		}

		if (stage === 'experience' && experienceAward) {
			return (
				<Awards
					title={experienceAward.title}
					amount={experienceAward.amount || 0}
					symbol={experienceAward.symbol || '$'}
					bonuses={experienceAward.bonuses || []}
					totalAmount={experienceAward.totalAmount || 0}
				/>
			)
		}

		if (stage === 'money' && moneyAward) {
			return (
				<Awards
					title={moneyAward.title}
					amount={moneyAward.amount || 0}
					symbol={moneyAward.symbol || '$'}
					bonuses={moneyAward.bonuses || []}
					totalAmount={moneyAward.totalAmount || 0}
				/>
			)
		}

		return null
	}

	return (
		<div className="hall-of-fame">
			<div className="hall-of-fame-container">
				{renderContent()}
			</div>
		</div>
	)
}